import * as React from 'react'
import { Plus, Trash2 } from 'lucide-react'
import type { SalesTemplatesDto } from '@lib/dto/sales-setup.dto'
import { CONTRACT_FIELDS, renderContract, SAMPLE_CONTRACT_DATA, templateErrors, type ContractField } from '@lib/helpers/contract-template'
import { putSalesTemplatesInputSchema } from '@lib/input/sales-setup.input'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { FormActions } from '@/components/ui/form-actions'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useSalesTemplates, useSaveSalesTemplates } from '@/hooks/useSales'

interface TemplateDraft {
  key: string
  id: string | undefined
  name: string
  body: string
}

let counter = 0
function newKey(): string {
  counter += 1
  return `new-${String(counter)}`
}

const STARTER = `# Event agreement

This agreement is between {{venue_name}} and {{couple_names}} for an event on {{event_date}}, {{event_time}}, for {{guest_count}} guests.

## What’s included

{{selections}}

{{totals}}

## Payments

{{payment_schedule}}

Security deposit: {{security_deposit}}

## Terms

Write your terms here.`

const FIELD_NAMES = Object.keys(CONTRACT_FIELDS).filter((name): name is ContractField => Object.hasOwn(CONTRACT_FIELDS, name))

/**
 * The fields a template can use, each with a button that puts it where the
 * cursor is.
 */
function FieldList({ onInsert }: { onInsert: (field: ContractField) => void }) {
  return (
    <ul className="m-0 list-none space-y-1 p-0 text-xs text-ink-muted">
      {FIELD_NAMES.map((name) => (
        <li key={name} className="flex flex-wrap items-baseline gap-x-2">
          <button type="button" className="shrink-0 font-mono text-accent-ink hover:underline" onClick={() => onInsert(name)}>
            {`{{${name}}}`}
          </button>
          <span>{CONTRACT_FIELDS[name]}</span>
        </li>
      ))}
    </ul>
  )
}

function Editor({ data, venueId, venueName }: { data: SalesTemplatesDto; venueId: string; venueName: string }) {
  const { toast } = useToast()
  const save = useSaveSalesTemplates(venueId)
  const [drafts, setDrafts] = React.useState<TemplateDraft[]>(data.templates.map((t) => ({ key: t.id, id: t.id, name: t.name, body: t.body })))
  const [selected, setSelected] = React.useState<string | null>(data.templates[0]?.id ?? null)
  const [error, setError] = React.useState<string | null>(null)
  const textarea = React.useRef<HTMLTextAreaElement>(null)
  const current = drafts.find((d) => d.key === selected) ?? null
  const canEdit = data.canEdit
  const preview = React.useMemo(
    () => (current === null ? '' : renderContract(current.body, { ...SAMPLE_CONTRACT_DATA, venueName })),
    [current, venueName],
  )
  const problems = current === null ? [] : templateErrors(current.body)

  function update(patch: Partial<TemplateDraft>): void {
    if (current === null) return
    setDrafts((all) => all.map((d) => (d.key === current.key ? { ...d, ...patch } : d)))
  }

  function insert(field: ContractField): void {
    if (current === null) return
    const at = textarea.current?.selectionStart ?? current.body.length
    const text = `{{${field}}}`
    update({ body: `${current.body.slice(0, at)}${text}${current.body.slice(at)}` })
  }

  function add(): void {
    const draft = { key: newKey(), id: undefined, name: 'New contract', body: STARTER }
    setDrafts((all) => [...all, draft])
    setSelected(draft.key)
  }

  function remove(): void {
    if (current === null) return
    const rest = drafts.filter((d) => d.key !== current.key)
    setDrafts(rest)
    setSelected(rest[0]?.key ?? null)
  }

  function handleSave(): void {
    const parsed = putSalesTemplatesInputSchema.safeParse({
      templates: drafts.map((d) => ({ ...(d.id === undefined ? {} : { id: d.id }), name: d.name, body: d.body })),
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Something here isn’t complete.')
      return
    }
    setError(null)
    save.mutate(parsed.data, {
      onSuccess: () => toast('Contract templates saved. Packets already sent keep their contract.'),
      onError: (e) => setError(requestErrorMessage(e)),
    })
  }

  return (
    <fieldset disabled={!canEdit} className="m-0 min-w-0 space-y-4 border-0 p-0">
      <div className="flex flex-wrap items-center gap-3">
        {drafts.length > 0 ? (
          <Select aria-label="Template" value={selected ?? ''} onChange={(e) => setSelected(e.target.value)}>
            {drafts.map((d) => (
              <option key={d.key} value={d.key}>
                {d.name === '' ? 'Untitled' : d.name}
              </option>
            ))}
          </Select>
        ) : null}
        <Button variant="outline" size="sm" onClick={add}>
          <Plus className="h-3.5 w-3.5" />
          New template
        </Button>
      </div>

      {current === null ? (
        <p className="m-0 text-sm text-ink-muted">No contract templates yet. A venue needs one before it can send a proposal.</p>
      ) : (
        <div className="grid gap-6 xl:grid-cols-2">
          <div className="min-w-0 space-y-3">
            <div className="flex items-end gap-3">
              <Field label="Name" htmlFor="template-name" className="flex-1">
                <Input id="template-name" value={current.name} onChange={(e) => update({ name: e.target.value })} />
              </Field>
              <Button variant="ghost" size="sm" aria-label={`Remove ${current.name}`} onClick={remove}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <Field
              label="Contract"
              htmlFor="template-body"
              hint="A blank line starts a paragraph. # for a heading, - for a list, **bold**. Signatures are added at the end."
            >
              <Textarea
                ref={textarea}
                id="template-body"
                rows={22}
                className="font-mono text-control leading-5"
                value={current.body}
                onChange={(e) => update({ body: e.target.value })}
              />
            </Field>
            {problems.length > 0 ? (
              <ul className="m-0 list-none space-y-1 p-0 text-sm text-danger-ink">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            ) : null}
            <div className="space-y-1.5">
              <p className="m-0 text-xs font-medium text-ink-muted">Fields</p>
              <FieldList onInsert={insert} />
            </div>
          </div>
          <div className="min-w-0 space-y-1.5">
            <p className="m-0 text-xs font-medium text-ink-muted">Preview, with made-up details</p>
            <iframe
              title="Contract preview"
              sandbox=""
              srcDoc={preview}
              className="h-[640px] w-full rounded-lg border border-line bg-white"
            />
          </div>
        </div>
      )}

      {error === null ? null : <FormError>{error}</FormError>}
      {canEdit ? (
        <FormActions>
          <Button onClick={handleSave} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save templates'}
          </Button>
        </FormActions>
      ) : null}
    </fieldset>
  )
}

/**
 * A venue's contract templates (docs/sales.spec.md, "Choosing and signing"):
 * its own terms, filled in from each packet. The venue brings the wording.
 */
export function ContractTemplatesSection({ venueId, venueName }: { venueId: string; venueName: string }) {
  const query = useSalesTemplates(venueId)
  if (query.isPending) return <DetailSkeleton />
  if (query.isError) return <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
  return <Editor key={query.dataUpdatedAt} data={query.data} venueId={venueId} venueName={venueName} />
}
